import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import NavigationHeader from './components/navigationHeader';
import PageContainer from './components/pageContainer';
import PageLayout from './components/pageLayout';
import PageBody from './components/pageBody';
import PageText from './components/pageText';
import PageFooter from './components/pageFooter';
import { useCart } from '../context/CartContext';
import { getData, getPrintsData, getStickersData, getAccessoriesData, getSheetsData } from './ProductPage';
import './ProductDetailPage.css';
import { API_URL } from '../config';

import { resolveImage as resolveImageShared } from '../data/imageMap';

const SIZES = ['XS', 'S', 'M', 'L', 'XL'];
const SHAPES = ['Round', 'Almond', 'Stiletto'];
const LENGTHS = ['Extra Short', 'Short', 'Medium'];

const toSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function findProduct(productId, category) {
  const allNails = getData().map((p) => ({ ...p, category: 'nails' }));
  const allPrints = getPrintsData().map((p) => ({ ...p, category: 'prints' }));
  const allStickers = getStickersData().map((p) => ({ ...p, category: 'stickers' }));
  const allAccessories = getAccessoriesData().map((p) => ({ ...p, category: 'accessories' }));
  const allSheets = getSheetsData().map((p) => ({ ...p, category: 'sheets' }));
  const all = [...allNails, ...allPrints, ...allStickers, ...allAccessories, ...allSheets];
  // map URL category segment to internal category values
  const categoryMap = { stickers: ['stickers', 'sheets'], nails: ['nails'], prints: ['prints'], crochet: ['accessories'] };
  const allowed = category ? (categoryMap[category] || [category]) : null;
  const pool = allowed ? all.filter((p) => allowed.includes(p.category)) : all;
  return pool.find((p) => p.id === productId) || pool.find((p) => toSlug(p.title) === productId);
}

const requireImage = (path) => {
  try { return resolveImageShared(path); } catch { return null; }
};

export const ProductDetailPage = () => {
  const { productId, productSlug, category } = useParams();
  const { addToCart } = useCart();
  const product = findProduct(productSlug || productId, category);

  const [selectedImage, setSelectedImage] = useState(0);
  const [size, setSize] = useState('');
  const [shape, setShape] = useState(product?.defaultShape || '');
  const [length, setLength] = useState(product?.defaultLength || '');
  const [notes, setNotes] = useState('');
  const [added, setAdded] = useState(false);
  const [soldOut, setSoldOut] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [printSize, setPrintSize] = useState('small');

  useEffect(() => {
    if (!product) return;
    fetch(`${API_URL}/api/inventory`)
      .then((r) => r.json())
      .then((d) => {
        if (product.category === 'nails' && (d.nailSlots ?? 5) <= 0) setSoldOut(true);
      })
      .catch(() => {});
  }, [product]);

  if (!product) {
    return (
      <PageLayout><PageContainer><PageBody>
        <NavigationHeader />
        <PageText>
          <div className="detail-empty">
            <p>product not found</p>
            <Link to="/shop" className="detail-back-link">back to shop</Link>
          </div>
        </PageText>
        <PageFooter />
      </PageBody></PageContainer></PageLayout>
    );
  }

  const isPrints = product.category === 'prints';
  const printPrice = isPrints ? (printSize === 'small' ? 12 : 30) : null;
  const isNails = product.category === 'nails';
  const canAdd = isNails ? (size && shape && length && !soldOut) : !soldOut;
  const gallery = product.gallery || [product.image];

  const handleAdd = () => {
    const options = isNails ? { size, shape, length, notes } : isPrints ? { printSize } : {};
    const price = isPrints ? printPrice : product.price;
    addToCart({
      id: isNails ? `${product.id}-${size}-${shape}-${length}` : isPrints ? `${product.id}-${printSize}` : product.id,
      productId: product.id,
      category: product.category,
      title: product.title,
      price,
      image: requireImage(product.image),
      imagePath: product.image,
      options,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const isStickers = product.category === 'stickers' || product.category === 'sheets';
  const isPngLocal = (p) => !isStickers && p.toLowerCase().endsWith('.png');

  const printAspect = printSize === 'large' ? (8 / 10) : (() => {
    if (!product.size) return (4 / 6);
    const parts = product.size.match(/([\d.]+)\s*x\s*([\d.]+)/i);
    return parts ? parseFloat(parts[1]) / parseFloat(parts[2]) : (4 / 6);
  })();

  return (
    <PageLayout>
      <PageContainer>
        <PageBody>
          <NavigationHeader />
          <PageText>
            <div className="detail-container">
              <div className="detail-image-col">
                {isPrints ? (
                  <div className={`print-frame${printSize === 'large' ? ' print-frame-border' : ''}${product.border && printSize !== 'large' ? ' print-frame-tb' : ''}`} style={{ aspectRatio: `${printAspect}` }}>
                    <img src={requireImage(gallery[selectedImage])} alt={product.title} draggable={false} onContextMenu={e => e.preventDefault()} className="print-frame-img" style={{ objectFit: printSize !== 'large' && product.cover ? 'cover' : 'contain' }} />
                  </div>
                ) : (
                  isStickers ? (
                    <div className="sticker-bg">
                      <img src={requireImage(gallery[selectedImage])} alt={product.title} draggable={false} onContextMenu={e => e.preventDefault()} className="detail-image" />
                    </div>
                  ) : (
                    <img src={requireImage(gallery[selectedImage])} alt={product.title} draggable={false} onContextMenu={e => e.preventDefault()} className={`detail-image${isPngLocal(gallery[selectedImage]) ? ' png-bg' : ''}`} />
                  )
                )}
                {gallery.length > 1 && (
                  <div className="detail-gallery">
                    {gallery.map((img, i) => (
                      <img
                        key={i}
                        src={requireImage(img)}
                        alt={`${product.title} ${i + 1}`}
                        className={`detail-gallery-thumb ${selectedImage === i ? 'active' : ''}${isPngLocal(img) ? ' png-bg' : ''}`}
                        onClick={() => setSelectedImage(i)}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="detail-info-col">
                <h1 className="detail-title">{product.title}</h1>
                <p className="detail-price">${isPrints ? printPrice : product.price}</p>
                {product.description && !isPrints && (
                  <p className="detail-description">
                    {isNails ? `shown in: ${product.description}` : product.description}
                  </p>
                )}
                {isPrints && (
                  <div className="detail-option">
                    <label>size</label>
                    <div className="detail-option-btns">
                      <button className={`option-btn ${printSize === 'small' ? 'selected' : ''}`} onClick={() => setPrintSize('small')}>{product.size || '5 x 7 in'}</button>
                      <button className={`option-btn ${printSize === 'large' ? 'selected' : ''}`} onClick={() => setPrintSize('large')}>{product.size2 || '8 x 10 in'}</button>
                    </div>
                  </div>
                )}
                {isNails && (
                  <>
                    <div className="detail-features">
                      <span className="detail-feature">✧ handmade & designed by joy</span>
                      <span className="detail-feature">✧ reusable</span>
                      <span className="detail-feature">✧ comes with a prep kit</span>
                      <span className="detail-feature">✧ made with Aprés gel-x tips</span>
                    </div>
                    <div className="detail-option">
                      <label>size</label>
                      <div className="detail-option-btns">
                        {SIZES.map((s) => (<button key={s} className={`option-btn ${size === s ? 'selected' : ''}`} onClick={() => setSize(s)}>{s}</button>))}
                      </div>
                    </div>
                    <div className="detail-option">
                      <label>shape</label>
                      <div className="detail-option-btns">
                        {SHAPES.map((s) => (<button key={s} className={`option-btn ${shape === s ? 'selected' : ''}`} onClick={() => setShape(s)}>{s}</button>))}
                      </div>
                    </div>
                    <div className="detail-option">
                      <label>length</label>
                      <div className="detail-option-btns">
                        {LENGTHS.filter((l) => l !== 'Extra Short' || shape === 'Round').map((l) => (<button key={l} className={`option-btn ${length === l ? 'selected' : ''}`} onClick={() => setLength(l)}>{l}</button>))}
                      </div>
                    </div>
                  </>
                )}
                {isNails && (
                  <div className="detail-option">
                    <label>notes (optional)</label>
                    <textarea className="detail-notes" placeholder="any special requests..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
                  </div>
                )}
                <button className="detail-add-btn" disabled={!canAdd} onClick={handleAdd}>
                  {soldOut ? 'sold out' : added ? 'added ✧' : `+ add to cart`}
                </button>
                {isNails && !canAdd && !soldOut && <p className="detail-hint">please select size, shape, and length</p>}
                <Link to="/shop" className="detail-back-link">← back to shop</Link>
                <div className="detail-tabs-inline">
                  <div className="detail-tab-bar">
                    {[
                      { key: 'description', label: 'description' },
                      { key: isNails ? 'sizing' : 'details', label: isNails ? 'sizing' : 'details' },
                      { key: 'about', label: 'about the artist' },
                    ].map((t) => (
                      <button key={t.key} className={`detail-tab${activeTab === t.key ? ' detail-tab-active' : ''}`} onClick={() => setActiveTab(t.key)}>{t.label}</button>
                    ))}
                  </div>
                  <div className="detail-tab-content">
                    {activeTab === 'description' && <p>{product.description || 'no description available.'}</p>}
                    {activeTab === 'sizing' && (
                      <div>
                        <p>Measure the widest part of each nail bed in millimeters:</p>
                        <table className="sizing-table">
                          <thead><tr><th>Size</th><th>Thumb</th><th>Index</th><th>Middle</th><th>Ring</th><th>Pinky</th></tr></thead>
                          <tbody>
                            <tr><td>XS</td><td>12mm</td><td>10mm</td><td>10mm</td><td>9mm</td><td>7mm</td></tr>
                            <tr><td>S</td><td>14mm</td><td>12mm</td><td>12mm</td><td>11mm</td><td>9mm</td></tr>
                            <tr><td>M</td><td>16mm</td><td>14mm</td><td>13mm</td><td>12mm</td><td>10mm</td></tr>
                            <tr><td>L</td><td>18mm</td><td>15mm</td><td>14mm</td><td>13mm</td><td>11mm</td></tr>
                            <tr><td>XL</td><td>20mm</td><td>17mm</td><td>16mm</td><td>15mm</td><td>13mm</td></tr>
                          </tbody>
                        </table>
                        <p className="sizing-tip">tip: if you're between sizes, size up. if you know your gel-x tip sizes, include them in the notes.</p>
                      </div>
                    )}
                    {activeTab === 'details' && (
                      <div>
                        {product.details ? product.details.split('·').map((d, i) => <p key={i}>✧ {d.trim()}</p>) : product.size ? <p>size: {product.size}</p> : <p>no details available.</p>}
                      </div>
                    )}
                    {activeTab === 'about' && (
                      <div>
                        <p>egg tarty is a small independent studio. everything is designed and made by joy.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </PageText>
          <div className="detail-bottom-section">
            <div className="detail-tabs-section detail-tabs-mobile">
              <div className="detail-tab-bar">
                {[
                  { key: 'description', label: 'description' },
                  { key: isNails ? 'sizing' : 'details', label: isNails ? 'sizing' : 'details' },
                  { key: 'about', label: 'about the artist' },
                ].map((t) => (
                  <button key={t.key} className={`detail-tab${activeTab === t.key ? ' detail-tab-active' : ''}`} onClick={() => setActiveTab(t.key)}>{t.label}</button>
                ))}
              </div>
              <div className="detail-tab-content">
                {activeTab === 'description' && <p>{product.description || 'no description available.'}</p>}
                {activeTab === 'sizing' && (
                  <div>
                    <p>Measure the widest part of each nail bed in millimeters:</p>
                    <table className="sizing-table">
                      <thead><tr><th>Size</th><th>Thumb</th><th>Index</th><th>Middle</th><th>Ring</th><th>Pinky</th></tr></thead>
                      <tbody>
                        <tr><td>XS</td><td>12mm</td><td>10mm</td><td>10mm</td><td>9mm</td><td>7mm</td></tr>
                        <tr><td>S</td><td>14mm</td><td>12mm</td><td>12mm</td><td>11mm</td><td>9mm</td></tr>
                        <tr><td>M</td><td>16mm</td><td>14mm</td><td>13mm</td><td>12mm</td><td>10mm</td></tr>
                        <tr><td>L</td><td>18mm</td><td>15mm</td><td>14mm</td><td>13mm</td><td>11mm</td></tr>
                        <tr><td>XL</td><td>20mm</td><td>17mm</td><td>16mm</td><td>15mm</td><td>13mm</td></tr>
                      </tbody>
                    </table>
                    <p className="sizing-tip">tip: if you're between sizes, size up. if you know your gel-x tip sizes, include them in the notes.</p>
                  </div>
                )}
                {activeTab === 'details' && (
                  <div>
                    {product.details ? product.details.split('·').map((d, i) => <p key={i}>✧ {d.trim()}</p>) : product.size ? <p>size: {product.size}</p> : <p>no details available.</p>}
                  </div>
                )}
                {activeTab === 'about' && (
                  <div>
                    <p>egg tarty is a small independent studio. everything is designed and made by joy.</p>
                  </div>
                )}
              </div>
            </div>
            <PageFooter />
          </div>
        </PageBody>
      </PageContainer>
    </PageLayout>
  );
};
